import { z, ZodType } from 'zod'

import { StaffPositionEnum } from '../../../../lib/types/enums/staff/staff-position.enum'
import { StaffQualificationEnum } from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import { DiplomaEnum } from '../../../../lib/types/enums/staff-application/diploma.enum'
import { PayScaleTypeEnum } from '../../../../lib/types/enums/staff-pay-scale/pay-scale-type.enum'
import { StaffPayScaleInput } from '../../../../lib/types/models/staff-pay-scale/staff-pay-scale-input'
import { NullableTransformer } from '../../../../lib/types/utils'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'

type NullablePositionStaffPayScaleInput = Omit<StaffPayScaleInput, 'position'> & {
  position?: StaffPositionEnum
}

export const staffPayScaleFormSchema: ZodType<
  NullableTransformer<NullablePositionStaffPayScaleInput>
> = z
  .object({
    name: createStringSchema('NOM', { nullish: true }),
    type: createEnumSchema({ fieldName: 'type', enumType: PayScaleTypeEnum, nullish: true }),
    position: createEnumSchema({
      fieldName: 'position',
      enumType: StaffPositionEnum,
      nullish: true
    }),
    dailySalaryFromSecondSlice: createNumberSchema('salaire 2', { nullish: true }),
    dailySalaryFromFirstSlice: createNumberSchema('salaire 1', { nullish: true }),
    bonusFromFirstSlice: createNumberSchema('prime 1 semaine', { nullish: true }),
    bonusFromSecondSlice: createNumberSchema('prime 2 semaines', { nullish: true }),
    qualifications: createEnumArraySchema({
      fieldName: 'qualifications',
      enumType: StaffQualificationEnum
    }).nullish(),
    diplomas: createEnumArraySchema({ fieldName: 'diplôme', enumType: DiplomaEnum }).nullish(),
    countriesId: createNumberArraySchema('pays', { minimumNumberOfItems: 1 }),
    formulasId: createNumberArraySchema('formules', { minimumNumberOfItems: 1 }),
    periodsId: createNumberArraySchema('périodes', { minimumNumberOfItems: 1 }),
    description: createStringSchema('texte à afficher', { nullish: true })
  })
  .superRefine((data, ctx) => {
    if (!data.name) {
      ctx.addIssue({
        path: ['name'],
        message: 'Veuillez entrer un nom',
        code: z.ZodIssueCode.custom
      })
    }

    if (!data.type) {
      ctx.addIssue({
        path: ['type'],
        message: 'Veuillez choisir un type',
        code: z.ZodIssueCode.custom
      })
    }

    if (!data.position) {
      ctx.addIssue({
        path: ['position'],
        message: 'Veuillez choisir une position',
        code: z.ZodIssueCode.custom
      })
    }

    if (data.type === PayScaleTypeEnum.BONUS) {
      if (!data.bonusFromFirstSlice) {
        ctx.addIssue({
          path: ['bonusFromFirstSlice'],
          message: "Veuillez entrer la prime pour les séjours d'1 semaine",
          code: z.ZodIssueCode.custom
        })
      }

      if (!data.bonusFromSecondSlice) {
        ctx.addIssue({
          path: ['bonusFromSecondSlice'],
          message: 'Veuillez entrer la prime pour les séjours de 2 semaines et plus',
          code: z.ZodIssueCode.custom
        })
      }

      if (!data.diplomas || data.diplomas.length === 0) {
        ctx.addIssue({
          path: ['diplomas'],
          message: 'Veuillez choisir au moins un diplôme',
          code: z.ZodIssueCode.custom
        })
      }
    }

    if (data.type === PayScaleTypeEnum.DAILY_SALARY) {
      if (!data.dailySalaryFromFirstSlice) {
        ctx.addIssue({
          path: ['dailySalaryFromFirstSlice'],
          message: 'Veuillez entrer un salaire',
          code: z.ZodIssueCode.custom
        })
      }

      if (!data.dailySalaryFromSecondSlice) {
        ctx.addIssue({
          path: ['dailySalaryFromSecondSlice'],
          message: 'Veuillez entrer un salaire',
          code: z.ZodIssueCode.custom
        })
      }

      if (!data.qualifications || data.qualifications.length === 0) {
        ctx.addIssue({
          path: ['qualifications'],
          message: 'Veuillez choisir une qualification',
          code: z.ZodIssueCode.custom
        })
      }
    }
  })
